<template>
  <teleport to="body">
    <div v-if="open" class="picker-overlay" @click.self="$emit('close')">
      <div class="picker-modal">
        <div class="picker-header">
          <h3 class="picker-title">{{ title }}</h3>
          <button type="button" class="picker-close" @click="$emit('close')">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="picker-search">
          <div class="search-input-wrap">
            <i class="ri-search-line search-icon"></i>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="搜尋商品名稱或 SKU..."
              @input="onSearchInput"
              ref="searchInputEl"
            />
          </div>
        </div>

        <div class="picker-body">
          <div v-if="loading" class="picker-empty">
            <div class="spinner"></div>
            <span>載入中...</span>
          </div>
          <div v-else-if="!warehouseId" class="picker-empty">
            <i class="ri-store-2-line"></i>
            <span>請先選擇出貨倉庫</span>
          </div>
          <div v-else-if="items.length === 0" class="picker-empty">
            <i class="ri-inbox-line"></i>
            <span>{{ searchQuery ? '找不到符合的庫存商品' : '此倉庫目前無庫存' }}</span>
          </div>
          <div v-else class="picker-list">
            <div
              v-for="inv in items"
              :key="inv.variantId"
              class="picker-item"
              :class="{ 'picker-item-disabled': inv.availableQuantity <= 0 }"
            >
              <div class="picker-item-main">
                <div class="picker-item-info">
                  <span class="picker-item-name">{{ inv.productName }}</span>
                  <div class="picker-item-meta">
                    <code v-if="inv.variantSku" class="picker-item-sku">{{ inv.variantSku }}</code>
                    <span v-if="formatAttributes(inv.variantAttributes)" class="picker-item-attrs">
                      {{ formatAttributes(inv.variantAttributes) }}
                    </span>
                  </div>
                </div>
                <div class="picker-item-right">
                  <div class="stock-info">
                    <span class="stock-label">可用庫存</span>
                    <strong :class="inv.availableQuantity <= 0 ? 'stock-danger' : 'stock-ok'">
                      {{ inv.availableQuantity }}
                    </strong>
                  </div>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="inv.availableQuantity <= 0"
                    @click="onSelect(inv)"
                  >
                    <i class="ri-add-line"></i> 加入
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="totalPages > 1" class="picker-pagination">
            <button class="btn btn-ghost btn-sm" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
              <i class="ri-arrow-left-s-line"></i>
            </button>
            <span class="picker-page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button class="btn btn-ghost btn-sm" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
              <i class="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { warehouseService } from '@/api/modules/warehouses.js';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '選擇調撥商品' },
  warehouseId: { type: String, default: '' },
});

const emit = defineEmits(['close', 'select']);

const searchQuery = ref('');
const loading = ref(false);
const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const PAGE_SIZE = 15;
const searchInputEl = ref(null);
let searchTimer = null;

watch(() => props.open, async (val) => {
  if (val) {
    searchQuery.value = '';
    currentPage.value = 1;
    await fetchInventory();
    await nextTick();
    searchInputEl.value?.focus();
  } else {
    items.value = [];
  }
});

watch(() => props.warehouseId, () => {
  if (props.open) {
    currentPage.value = 1;
    fetchInventory();
  }
});

async function fetchInventory() {
  if (!props.warehouseId) { items.value = []; return; }
  loading.value = true;
  try {
    const res = await warehouseService.getWarehouseInventory(props.warehouseId, {
      page: currentPage.value,
      pageSize: PAGE_SIZE,
      search: searchQuery.value || undefined,
    });
    items.value = res.data?.items || [];
    totalPages.value = res.data?.pagination?.totalPages || 1;
  } catch (e) {
    console.error(e);
    items.value = [];
  } finally {
    loading.value = false;
  }
}

function onSearchInput() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchInventory();
  }, 300);
}

function changePage(page) {
  currentPage.value = page;
  fetchInventory();
}

function formatAttributes(attrs) {
  if (!attrs || typeof attrs !== 'object') return '';
  const vals = Object.values(attrs).filter(Boolean);
  return vals.join(' / ');
}

function onSelect(inv) {
  emit('select', inv);
  emit('close');
}
</script>

<style scoped>
.picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.picker-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 620px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}
.picker-title { font-size: 16px; font-weight: 600; margin: 0; }

.picker-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}
.picker-close:hover { background: #f0f0f0; }

.picker-search {
  padding: 12px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}
.search-input-wrap { position: relative; }
.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
}
.search-input-wrap .form-control { padding-left: 34px; }

.picker-body { flex: 1; overflow-y: auto; padding: 4px 0; }

.picker-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 32px;
  color: #999;
  font-size: 14px;
  flex-direction: column;
}
.picker-empty i { font-size: 32px; }

.picker-item { border-bottom: 1px solid #f0f0f0; }
.picker-item:last-child { border-bottom: none; }
.picker-item-disabled { opacity: 0.55; background: #fafafa; }

.picker-item-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  gap: 12px;
}

.picker-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.picker-item-name {
  font-weight: 500;
  font-size: 14px;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.picker-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.picker-item-sku { font-size: 12px; color: #888; }
.picker-item-attrs { font-size: 12px; color: #666; }

.picker-item-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.stock-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.stock-label { font-size: 11px; color: #999; }
.stock-ok { font-size: 15px; color: #27ae60; }
.stock-danger { font-size: 15px; color: #e74c3c; }

.picker-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #f0f0f0;
}
.picker-page-info { font-size: 13px; color: #666; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.btn { padding: 7px 14px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; display: inline-flex; align-items: center; gap: 4px; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #1558b0; }
.btn-ghost { background: none; border: 1px solid #d0d0d0; color: #555; }
.btn-ghost:hover:not(:disabled) { background: #f5f5f5; }
.btn-sm { padding: 4px 10px; font-size: 13px; border-radius: 4px; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #eee;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
